import { CLI_API_PREFIX } from "../constants.js";
import type { ResolvedConfig } from "../config.js";
import { HttpClient } from "./client.js";

export interface MetaResponse {
  request_id: string;
  service: string;
  api_version: string;
  platform_url: string;
  environment: string;
  min_cli_version: string;
  recommended_cli_version: string;
  features: Record<string, boolean>;
}

export interface MeResponse {
  request_id: string;
  user_id?: string;
  account?: string;
  email?: string;
  username?: string;
  is_admin?: boolean;
  user?: {
    id: string;
    display_name?: string;
    email?: string;
  };
  scopes?: string[];
}

export interface TokenResponse {
  request_id: string;
  access_token: string;
  refresh_token?: string;
  token_type: string;
  expires_in?: number;
  scope?: string;
  user?: {
    id: string;
    display_name?: string;
    email?: string;
  };
}


export interface RequestLogItem {
  request_id?: string;
  method: string;
  path: string;
  request_body?: unknown;
  response_status?: number;
  duration_ms?: number;
  created_at: string;
}

export interface RequestLogsResponse {
  request_id: string;
  items: RequestLogItem[];
  next_cursor?: string | null;
}

export interface PluginListItem {
  plugin_id: string;
  name?: string;
  description?: string;
  summary?: string;
  source?: "official" | "verified" | "community";
  tech_type?: string;
  category?: string;
  author_display_name?: string;
  latest_version?: string;
  downloads?: number;
  public_url?: string;
  updated_at?: string;
  [key: string]: unknown;
}

export interface PluginListResponse {
  request_id: string;
  items: PluginListItem[];
  next_cursor?: string | null;
}

export interface PluginVersionItem {
  version: string;
  published_at?: string;
  deprecated?: boolean;
  [key: string]: unknown;
}

export interface PluginVersionsResponse {
  request_id: string;
  plugin_id: string;
  items: PluginVersionItem[];
}

export interface PluginInfoResponse {
  request_id: string;
  plugin_id: string;
  install_command?: string;
  public_url?: string;
  latest_version?: string;
  scan_status?: string;
  min_qwenpaw_version?: string;
  [key: string]: unknown;
}

export interface SkillListItem {
  skill_id: string;
  skill_code?: string;
  name?: string;
  description?: string;
  summary?: string;
  category?: string;
  category_label?: string;
  source?: "official" | "verified" | "community";
  author_display_name?: string;
  latest_version?: string;
  downloads?: number;
  public_url?: string;
  updated_at?: string;
  [key: string]: unknown;
}

export interface SkillListResponse {
  request_id: string;
  items: SkillListItem[];
  next_cursor?: string | null;
}

export interface SkillVersionItem {
  version: string;
  published_at?: string;
  deprecated?: boolean;
  [key: string]: unknown;
}

export interface SkillVersionsResponse {
  request_id: string;
  skill_id: string;
  items: SkillVersionItem[];
}

export interface SkillInfoResponse {
  request_id: string;
  skill_id: string;
  install_command?: string;
  public_url?: string;
  latest_version?: string;
  scan_status?: string;
  [key: string]: unknown;
}

export interface InstallIntentResponse {
  request_id: string;
  download_url: string;
  sha512?: string;
  expires_at?: string;
  signature?: string | null;
  scan_status?: string;
  version?: string;
}

export interface CliSubmissionUpload {
  method?: string;
  url: string;
  headers?: Record<string, string>;
  expires_at?: string;
}

export interface CliSubmissionScan {
  status?: "queued" | "running" | "passed" | "failed";
  summary?: string;
  [key: string]: unknown;
}

export interface CliSubmissionPublish {
  status?: string;
  public_url?: string;
  [key: string]: unknown;
}

export interface CliSubmissionFailure {
  code?: string;
  message?: string;
}

export interface CliSubmissionResponse {
  request_id: string;
  submission_id: string;
  status:
    | "upload_url_issued"
    | "queued_for_scan"
    | "scanning"
    | "published"
    | "scan_failed"
    | "upload_failed"
    | "expired";
  terminal: boolean;
  upload?: CliSubmissionUpload | null;
  scan?: CliSubmissionScan | null;
  publish?: CliSubmissionPublish | null;
  failure?: CliSubmissionFailure | null;
  links?: { status?: string; logs?: string };
  next_poll_after_seconds?: number;
}

export interface CliSubmissionLogItem {
  level: "info" | "error" | string;
  message: string;
  timestamp: string;
}

export interface CliSubmissionLogsResponse {
  request_id: string;
  submission_id: string;
  items: CliSubmissionLogItem[];
}

export class CliApiClient {
  private readonly http: HttpClient;

  constructor(config: ResolvedConfig) {
    this.http = new HttpClient(config);
  }

  private req<T>(opts: Parameters<HttpClient["request"]>[0]): Promise<T> {
    return this.http.request<T>({ ...opts, prefix: CLI_API_PREFIX });
  }

  getMeta(): Promise<MetaResponse> {
    return this.req({ path: "/meta", auth: false });
  }

  getMe(): Promise<MeResponse> {
    return this.req({ path: "/me" });
  }

  login(account: string, password: string): Promise<TokenResponse> {
    return this.req({
      method: "POST",
      path: "/auth/login",
      auth: false,
      body: { account, password },
    });
  }

  refresh(refreshToken: string): Promise<TokenResponse> {
    return this.req({
      method: "POST",
      path: "/auth/refresh",
      auth: false,
      body: { refresh_token: refreshToken },
    });
  }

  oauthToken(body: Record<string, string>): Promise<TokenResponse> {
    return this.req({ method: "POST", path: "/oauth/token", auth: false, body });
  }

  oauthRevoke(token: string, tokenTypeHint = "refresh_token"): Promise<{ ok: boolean }> {
    return this.req<{ ok: boolean }>({
      method: "POST",
      path: "/oauth/revoke",
      body: { token, token_type_hint: tokenTypeHint },
    });
  }

  async logout(): Promise<{ ok: boolean }> {
    try {
      return await this.req<{ ok: boolean }>({ method: "POST", path: "/auth/logout" });
    } catch {
      return this.oauthRevoke("", "access_token");
    }
  }

  getRequestLogs(params?: {
    page_size?: number;
    cursor?: string;
  }): Promise<RequestLogsResponse> {
    const query = new URLSearchParams();
    if (params?.page_size !== undefined) query.set("page_size", String(params.page_size));
    if (params?.cursor) query.set("cursor", params.cursor);
    const suffix = query.toString();
    return this.req({ path: `/request-logs${suffix ? `?${suffix}` : ""}` });
  }

  searchPlugins(params?: {
    q?: string;
    category?: string;
    source?: "official" | "verified" | "community";
    sort?: "recommended" | "downloads" | "updated_at";
    page_size?: number;
    cursor?: string;
  }): Promise<PluginListResponse> {
    const query = new URLSearchParams();
    if (params?.q) query.set("q", params.q);
    if (params?.category) query.set("category", params.category);
    if (params?.source) query.set("source", params.source);
    if (params?.sort) query.set("sort", params.sort);
    if (params?.page_size !== undefined) query.set("page_size", String(params.page_size));
    if (params?.cursor) query.set("cursor", params.cursor);
    const suffix = query.toString();
    return this.req({ path: `/plugins${suffix ? `?${suffix}` : ""}`, auth: false });
  }

  getPluginInfo(pluginId: string, version?: string): Promise<PluginInfoResponse> {
    const query = new URLSearchParams();
    if (version) query.set("version", version);
    const suffix = query.toString();
    return this.req({
      path: `/plugins/${encodeURIComponent(pluginId)}${suffix ? `?${suffix}` : ""}`,
      auth: false,
    });
  }

  getPluginVersions(pluginId: string): Promise<PluginVersionsResponse> {
    return this.req({
      path: `/plugins/${encodeURIComponent(pluginId)}/versions`,
      auth: false,
    });
  }

  createInstallIntent(pluginId: string, version?: string): Promise<InstallIntentResponse> {
    return this.req({
      method: "POST",
      path: `/plugins/${encodeURIComponent(pluginId)}/install-intent`,
      body: version ? { version } : {},
    });
  }

  searchSkills(params?: {
    q?: string;
    category?: string;
    source?: "official" | "verified" | "community";
    tag?: string;
    sort?: "recommended" | "downloads" | "updated_at";
    page_size?: number;
    cursor?: string;
  }): Promise<SkillListResponse> {
    const query = new URLSearchParams();
    if (params?.q) query.set("q", params.q);
    if (params?.category) query.set("category", params.category);
    if (params?.source) query.set("source", params.source);
    if (params?.tag) query.set("tag", params.tag);
    if (params?.sort) query.set("sort", params.sort);
    if (params?.page_size !== undefined) query.set("page_size", String(params.page_size));
    if (params?.cursor) query.set("cursor", params.cursor);
    const suffix = query.toString();
    return this.req({ path: `/skills${suffix ? `?${suffix}` : ""}`, auth: false });
  }

  getSkillInfo(skillId: string, version?: string): Promise<SkillInfoResponse> {
    const query = new URLSearchParams();
    if (version) query.set("version", version);
    const suffix = query.toString();
    return this.req({
      path: `/skills/${encodeURIComponent(skillId)}${suffix ? `?${suffix}` : ""}`,
      auth: false,
    });
  }

  getSkillVersions(skillId: string): Promise<SkillVersionsResponse> {
    return this.req({
      path: `/skills/${encodeURIComponent(skillId)}/versions`,
      auth: false,
    });
  }

  createSkillInstallIntent(skillId: string, version?: string): Promise<InstallIntentResponse> {
    return this.req({
      method: "POST",
      path: `/skills/${encodeURIComponent(skillId)}/install-intent`,
      body: version ? { version } : {},
    });
  }

  createArtifactSubmission(body: Record<string, unknown>): Promise<CliSubmissionResponse> {
    return this.req({
      method: "POST",
      path: "/artifact-submissions",
      body,
    });
  }

  completeArtifactSubmission(
    submissionId: string,
    body: { sha512: string; size_bytes: number },
  ): Promise<CliSubmissionResponse> {
    return this.req({
      method: "POST",
      path: `/artifact-submissions/${encodeURIComponent(submissionId)}/complete`,
      body,
    });
  }

  getArtifactSubmission(submissionId: string): Promise<CliSubmissionResponse> {
    return this.req({
      path: `/artifact-submissions/${encodeURIComponent(submissionId)}`,
    });
  }

  getArtifactSubmissionLogs(submissionId: string): Promise<CliSubmissionLogsResponse> {
    return this.req({
      path: `/artifact-submissions/${encodeURIComponent(submissionId)}/logs`,
    });
  }
}
